'use client';

import {
  DataTable,
  DataTableColumn,
  TableEmptyState
} from '@/components/ui/DataTable';
import { Button } from '@/components/ui/Button';
import { TextLink } from '@/components/ui/TextLink';
import { InterviewWithStats } from '@/types/pulsedb';
import { useSupaClient, useWorkspace } from '@/utils/clerk/client';
import { formatDateTime } from '@/utils/helpers';
import { deleteInterview } from '@/utils/supabase/actions';
import { getInterviewsWithStats } from '@/utils/supabase/queries';
import { ArrowRightIcon } from 'lucide-react';
import { useRouter } from 'next/navigation';
import useSWR from 'swr';
import InterviewActionDropdown from './helpers/InterviewActionDropdown';
import InterviewBadge from './helpers/InterviewBadge';

export function InterviewsTable({ limit }: { limit?: number }) {
  const { workspace } = useWorkspace();
  const supaClient = useSupaClient();
  const router = useRouter();

  const swrKey =
    workspace && supaClient
      ? `/api/workspaces/${workspace.id}/interviews-with-stats/${limit}`
      : null;

  const { data: interviews, mutate } = useSWR(
    swrKey,
    workspace && supaClient
      ? () => getInterviewsWithStats(supaClient, workspace.id, limit)
      : null
  );

  const handleDelete = async (interviewId: number) => {
    await deleteInterview(interviewId);
    mutate();
  };

  const columns: DataTableColumn<InterviewWithStats>[] = [
    {
      key: 'name',
      header: 'Interview',
      truncate: true,
      cell: (interview) => (
        <span title={interview.name ?? undefined}>{interview.name}</span>
      ),
      cellClassName: 'font-medium'
    },
    {
      key: 'status',
      header: 'Status',
      cell: (interview) => <InterviewBadge status={interview.status} />
    },
    {
      key: 'created_at',
      header: 'Created At',
      cell: (interview) => formatDateTime(interview.created_at)
    },
    {
      key: 'last_complete',
      header: 'Last Complete',
      sortable: true,
      sortValue: (interview) => interview.last_completed_at,
      firstSortDirection: 'desc',
      cell: (interview) =>
        interview.last_completed_at
          ? formatDateTime(interview.last_completed_at)
          : '-'
    },
    {
      key: 'conversations',
      header: 'Conversations',
      align: 'right',
      sortable: true,
      sortValue: (interview) => interview.completed_count,
      firstSortDirection: 'desc',
      interactive: true,
      cell: (interview) => (
        <TextLink
          variant="quiet"
          href={`/account/interview/${interview.slug}/conversation?s=40`}
        >
          {interview.completed_count} completed
        </TextLink>
      )
    },
    {
      key: 'actions',
      header: 'Actions',
      srOnlyHeader: true,
      align: 'right',
      interactive: true,
      cellClassName: 'pl-20',
      cell: (interview) => (
        <InterviewActionDropdown
          interview={interview}
          variant="horizontal"
          onDeleted={handleDelete}
          onDuplicated={() => mutate()}
          cntConversations={interview.completed_count}
        />
      )
    }
  ];

  return (
    <>
      <DataTable
        columns={columns}
        rows={interviews}
        rowKey={(interview) => interview.id}
        onRowClick={(interview) =>
          router.push(`/account/interview/${interview.slug}`)
        }
        empty={
          <TableEmptyState
            title="No interviews yet."
            action={
              <Button onClick={() => router.push('/account/interview/add')}>
                Create your first interview
              </Button>
            }
          />
        }
      />
      {limit && interviews && interviews.length === limit && (
        <div className="mt-4 text-sm text-zinc-400">
          <TextLink variant="quiet" href="/account/interview">
            View all Interviews
            <ArrowRightIcon size={16} className="ml-2 inline-block" />
          </TextLink>
        </div>
      )}
    </>
  );
}
